import { Modal } from '@foam/buoy/src/components/Modal/Modal';
import { Toggle } from '@foam/buoy/src/components/Toggle/Toggle';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Feature } from '~/components/Feature';
import { MultiselectBar } from '~/components/MultiselectBar';
import { NewConfirmationModal } from '~/components/NewConfirmationModal';
import { FEATURE_FLAGS } from '~/constants';
import { CONTENT_SEARCH_AD_HASHTAGS } from '~/hooks/useContentSearch';
import { useContentSelection } from '~/screens/PageBuilder/blocks/TalentContent/useContentSelection';
import {
  AddContentWithMetrics,
  FilterType,
  Platforms,
  Posted,
  SelectedContentThumbnails,
  SortBy,
} from '~/screens/PageBuilder/sidebar/ModalAddContent';
import { useAddContentStore } from '~/screens/PageBuilder/sidebar/useAddContentStore';
import { ContentsApiResponseResultsType } from '~/searchApi';

import { SearchInput } from './SearchInput';

export const ModalAddContent = () => {
  const {
    isModalOpen,
    isEditMode,
    filters,
    showInShared,
    setIsModalOpen,
    setFilters,
    removeFilter,
    setSearchQuery,
    setShowInShared,
    page,
    setPage,
    reset,
  } = useAddContentStore();

  const {
    baseContents,
    hasChanges: hasContentChanges,
    handleClose,
    handleAddEditContent,
    handleSelectContent,
  } = useContentSelection();

  const contentRef = useRef<HTMLDivElement>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
    },
    [setSearchQuery],
  );

  // Scroll modal content to top when pagination page changes
  useEffect(() => {
    let el = contentRef.current?.parentElement;
    while (el) {
      const style = window.getComputedStyle(el);
      if (style.overflow === 'auto' || style.overflowY === 'auto') {
        el.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      }
      el = el.parentElement;
    }
  }, [page]);

  useEffect(() => {
    return () => {
      // Reset the modal when it unmounts
      reset();
      setPage(1);
    };
  }, [reset, setPage]);

  const hasChanges = useMemo(() => {
    if (isEditMode) {
      // In edit mode: check if contents actually changed from the original
      return hasContentChanges;
    }
    // In add mode: check if there are selected contents
    return baseContents.length > 0;
  }, [isEditMode, hasContentChanges, baseContents.length]);

  const handleModalClose = useCallback(() => {
    // Show confirmation only if there are changes
    if (hasChanges) {
      setShowConfirmation(true);
      return;
    }
    setIsModalOpen(false, undefined);
    reset();
  }, [setIsModalOpen, reset, hasChanges]);

  const handleConfirmLeave = useCallback(() => {
    setShowConfirmation(false);
    setIsModalOpen(false, undefined);
    reset();
  }, [setIsModalOpen, reset]);

  const handleCancelLeave = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  const handleToggle = useCallback(
    (checked: boolean) => {
      const paidFilter: FilterType = {
        comparator: 'contains',
        name: 'hashtags',
        value: CONTENT_SEARCH_AD_HASHTAGS,
      };

      if (checked) {
        setFilters(paidFilter);
        return;
      }
      removeFilter('hashtags');
    },
    [setFilters, removeFilter],
  );

  const isPaidToggleChecked = useMemo(() => {
    const isChecked = filters.some((f) => f.name === 'hashtags');
    return isChecked;
  }, [filters]);

  const handleToggleShowInShared = useCallback(() => {
    setShowInShared(!showInShared);
  }, [showInShared, setShowInShared]);

  const handleRemoveContent = useCallback(
    (content: ContentsApiResponseResultsType) => {
      handleSelectContent(false, content);
    },
    [handleSelectContent],
  );

  const isSaveDisabled = useMemo(() => {
    if (isEditMode) {
      // In edit mode: disable if no changes were made
      return !hasContentChanges;
    }
    // In add mode: disable if no items selected
    return baseContents.length === 0;
  }, [isEditMode, hasContentChanges, baseContents.length]);

  return (
    <>
      <Modal.Instance open={isModalOpen} onOpenChange={handleModalClose}>
        <Modal.Root
          title={`${isEditMode ? 'Edit' : 'Add'} content`}
          size="full"
        >
          <NewConfirmationModal
            isOpen={showConfirmation}
            onConfirm={handleConfirmLeave}
            onCancel={handleCancelLeave}
          />
          <div
            ref={contentRef}
            className="flex -translate-y-1 flex-col gap-4 overflow-x-clip bg-neutral-0 pb-16"
          >
            <div className="sticky top-0 z-50 flex flex-col gap-4 bg-neutral-0 px-2 py-1">
              <div className="flex justify-between">
                <div className="flex items-center justify-center gap-2">
                  <Platforms />

                  <Posted />
                  <SortBy />

                  {/* Paid Content */}
                  <Toggle
                    id="paid-content-toggle"
                    heading="Paid Content"
                    labelPosition="right"
                    checked={isPaidToggleChecked}
                    onCheckedChange={handleToggle}
                    aria-label="Paid Content"
                  />
                </div>

                <SearchInput onSearch={handleSearch} />
              </div>

              <Feature name={FEATURE_FLAGS.foamContentSelection}>
                <SelectedContentThumbnails
                  contents={baseContents}
                  onRemove={handleRemoveContent}
                />
              </Feature>
            </div>

            <AddContentWithMetrics />
          </div>

          <MultiselectBar
            isOpen
            numberOfItems={baseContents.length}
            showInShared={showInShared}
            onClose={handleClose}
            onClick={handleAddEditContent}
            onToggle={handleToggleShowInShared}
            disabled={isSaveDisabled}
          />
        </Modal.Root>
      </Modal.Instance>
    </>
  );
};
